import { Strategy } from 'workbox-strategies';

class HTTPError extends Error {
    constructor(status, message) {
      super(message);
      this.name = 'HTTPError';
      this.status = status;
    }
  }

export async function findResponseInCache(partialUrl) {
    const cache = await caches.open('logged-in-cached-data');
    const requests = await cache.keys(); // Obtenez toutes les requêtes en cache

    // Créer un tableau pour stocker les paires request/response avec leurs dates
    const requestWithDates = [];

    for (const request of requests) {
        if (request.url.includes(partialUrl)) {
            const response = await cache.match(request);
            if (response) {
                const dateHeader = response.headers.get('X-Cache-Date');
                requestWithDates.push({ request, response, date: new Date(dateHeader) });
            }
        }
    }

    // Trier par date, les plus récentes d'abord
    requestWithDates.sort((a, b) => b.date - a.date);

    // Retourner la première réponse triée si disponible
    return requestWithDates.length > 0 ? requestWithDates[0].response : null;
}


async function fetchWithTimeout(request, timeout) {
    // AbortSignal.timeout is supported only for browsers > 2022
    // polyfill
    if (timeout !==null) {
        const controller = new AbortController();
        const signal = controller.signal;
        const timer = setTimeout(() => {
            console.log(`fetchWithTimeout ${request.url} Fetch aborted`)
            controller.abort();
        }, timeout);

        try {
            const response = await fetch(request, { signal });
            // Attendre le téléchargement complet (par exemple, en tant que blob)
            const clonedResponse = response.clone();
            await clonedResponse.blob();
            return response;
          } finally {
            clearTimeout(timer);
          }
    } else {
        return fetch(request)
    }

}

async function updateCacheWithNetworkResponse(request, networkResponse, handler) {
    // Put it into cache
    const responseToCache = networkResponse.clone();
    console.log(`updateCacheWithNetworkResponse : ${responseToCache.status}`)

    if (responseToCache.status >= 200 && responseToCache.status < 210) {
        // Add a custom header with the current date/time
        const customHeaders = new Headers(responseToCache.headers);
        customHeaders.append('X-Cache-Date', new Date().toISOString());

        const responseWithCustomHeader = new Response(responseToCache.status === 204 ? null : responseToCache.body, {
            status: responseToCache.status,
            statusText: responseToCache.statusText,
            headers: customHeaders
        });

        // Update cache
        try {
            await handler.cachePut(request, responseWithCustomHeader);
        } catch(err) {
            console.log(`updateCacheWithNetworkResponse : failed to update cache with network response ${err}`)
        }
    } else {
        throw new HTTPError(responseToCache.status, 'updateCacheWithNetworkResponse : Network request returned an HTTP error');
    }
}


/**
 * Stratégie Custom Workbox
 * The base workbox NetworkFirst strategy allows a networkTimeout that cancels the requests if the server does not answer in time and then attempts to get the request from cache.
 * The problem is : is relies only on the "Waiting for server response" time,
 * and does not take into account large timings of response reception
 *  (for example if the network bandwith is limited, response download time could be high,
 *  even if the server responds quickly)
 */
class NetworkFirstWithDownloadTimeout extends Strategy {
    constructor(options = {}) {
        super(options);
        // On récupère le paramètre downloadTimeout dans les options, avec une valeur par défaut de 2000 ms
        this.downloadTimeout = options.downloadTimeout;
      }

    async _handle(request, handler) {
        console.log(`NetworkFirstWithDownloadTimeout ${request.url} timeout ${this.downloadTimeout} ms`)
        const cacheResponse = await findResponseInCache(request.url);
        if (cacheResponse) {
            console.log(`NetworkFirstWithDownloadTimeout ${request.url} : cache found, but requesting network`)
            // First, attempting to fetch the response from the network
            return fetchWithTimeout(request, this.downloadTimeout).then(async (networkResponse)=> {
                try {
                    await updateCacheWithNetworkResponse(request, networkResponse, handler)
                    console.log(`NetworkFirstWithDownloadTimeout ${request.url} : cache updated with network request`)
                    return networkResponse
                }catch (exception) {
                    if (exception instanceof HTTPError) {
                        console.log(`NetworkFirstWithDownloadTimeout ${request.url} : got HTTP error`)
                        // HTTP error, we serve the request as it is
                        return networkResponse
                    }else {
                        console.log(`NetworkFirstWithDownloadTimeout ${request.url} : Unexpected error`, exception);
                        throw exception
                    }
                }

            }).catch(async (exception) => {
                // Timeout was reached or network error
                // Serving cache
                console.log(`NetworkFirstWithDownloadTimeout ${request.url} : fetching failed fallback to cache`, exception);
                console.log(`NetworkFirstWithDownloadTimeout ${request.url} : cache served`)
                return cacheResponse;
            });
        }else {
            console.log(`NetworkFirstWithDownloadTimeout ${request.url} : No response in cache already, performing a fetch without timeout`)
            try {
                const networkResponse = await fetch(request)
                await updateCacheWithNetworkResponse(request, networkResponse, handler)
                console.log(`NetworkFirstWithDownloadTimeout ${request.url} : cache updated with network request`)
                //Potentially a network error can occur
                return networkResponse
            }catch (err){
                return new Response('Network error and no cache available', {
                    status: 503,
                    statusText: 'Service Unavailable'
                });
            }
        }
    }
}

export { NetworkFirstWithDownloadTimeout };